'use client';
import { fmtCap, capToSlider, sliderToCap } from '@/lib/market-cap-slider';

/**
 * Dual-thumb market cap range slider. Self-contained — only depends on the
 * market-cap-slider helpers and the parent's [min, max] state via callbacks.
 */
export function MarketCapSlider({
  minMarketCap,
  maxMarketCap,
  onMinChange,
  onMaxChange,
}: {
  minMarketCap: number;
  maxMarketCap: number;
  onMinChange: (v: number) => void;
  onMaxChange: (v: number) => void;
}) {
  const sliderValMin = capToSlider(minMarketCap);
  const sliderValMax = capToSlider(maxMarketCap);
  // Compact layout: title row with current range + single inline slider row
  // with $5B / $10T anchors flanking it. Steve directive 2026-05-08: rarely-used
  // control was eating too much vertical space; collapsed from 3 rows to 2.
  return (
    <div className="px-5 py-1.5 border-b border-[#1e293b]">
      <div className="flex items-baseline justify-between mb-1">
        <label className="text-[#e2e8f0] text-[10px] font-semibold uppercase tracking-widest">
          Market Cap
        </label>
        <span className="text-white text-[11px] font-semibold tabular-nums" style={{ fontFamily: 'var(--font-mono)' }}>
          {fmtCap(minMarketCap)} – {fmtCap(maxMarketCap)}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[#94a3b8] text-[9px] tabular-nums w-7 text-right">$5B</span>
        <div className="relative h-5 flex-1">
          <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1 rounded-full bg-[#1e293b]" />
          <div
            className="absolute top-1/2 -translate-y-1/2 h-1 rounded-full bg-blue-500"
            style={{ left: `${sliderValMin}%`, right: `${100 - sliderValMax}%` }}
          />
          <input
            type="range"
            min={0}
            max={100}
            value={sliderValMin}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v < sliderValMax) onMinChange(sliderToCap(v));
            }}
            className="absolute w-full top-0 h-5 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-10"
          />
          <input
            type="range"
            min={0}
            max={100}
            value={sliderValMax}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v > sliderValMin) onMaxChange(sliderToCap(v));
            }}
            className="absolute w-full top-0 h-5 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-20"
          />
        </div>
        <span className="text-[#94a3b8] text-[9px] tabular-nums w-7">$10T</span>
      </div>
    </div>
  );
}
